import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock 
} from 'lucide-react';

// Official Clean Social Media SVGs
const LinkedinIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.25V10.9H6.46M7.86 6.78a1.6 1.6 0 1 0 1.6 1.6 1.6 1.6 0 0 0-1.6-1.6z" />
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H7.5v-3H10V9.5C10 7.01 11.49 5.6 13.78 5.6c1.1 0 2.25.2 2.25.2v2.47h-1.27c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 3h-2.33v6.8c4.56-.93 8-4.96 8-9.8z" />
  </svg>
);

const TwitterIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

export default function Footer() {
  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about' },
    { name: 'Services', href: '/services/software-development' },
    { name: 'Case Studies', href: '/case-studies' },
    { name: 'Industries', href: '/industries' },
    { name: 'Contact Us', href: '/contact' },
  ];

  const services = [
    { name: 'Software Development', href: '/services/software-development' },
    { name: 'Cloud Solutions', href: '/services/cloud-solutions' },
    { name: 'Cyber Security', href: '/services/cyber-security' },
    { name: 'IT Consulting', href: '/services/it-consulting' },
    { name: 'Managed IT Services', href: '/services/managed-it-services' },
  ];

  const industries = [
    { name: 'Healthcare', href: '/industries' },
    { name: 'Finance', href: '/industries' },
    { name: 'Retail', href: '/industries' },
    { name: 'Manufacturing', href: '/industries' },
    { name: 'Education', href: '/industries' },
    { name: 'Logistics', href: '/industries' },
  ];

  return (
    <footer className="w-full bg-[#161616] text-gray-400 font-sans text-xs border-t-4 border-[#f25c05]">
      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-6">
          
          {/* Column 1: Logo & Company Description */}
          <div className="lg:col-span-4 space-y-4">
            <Link to="/" className="inline-flex items-center space-x-3">
              <div className="flex items-center justify-center text-[#f25c05]">
                <svg className="w-8 h-8 fill-current" viewBox="0 0 24 24">
                  <path d="M12 2L2 22h20L12 2zm0 4.5l6.5 13.5h-13L12 6.5z" />
                  <path d="M12 9l-3.5 7h7L12 9z" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-extrabold tracking-wider text-white uppercase leading-none">
                  INIM.AI
                </span>
                <span className="text-[10px] tracking-[0.25em] font-semibold text-gray-400 uppercase leading-tight mt-0.5">
                  SOLUTIONS
                </span>
              </div>
            </Link>

            <p className="text-gray-400 leading-relaxed max-w-sm">
              Inimai Solutions is an AI-first technology company delivering innovative, scalable and secure solutions to help businesses innovate, automate and grow.
            </p>

            {/* Social Links */}
            <div className="flex items-center space-x-3 pt-2">
              <a
                href="#"
                className="w-7 h-7 rounded-full bg-white text-[#161616] flex items-center justify-center hover:bg-[#f25c05] hover:text-white transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <LinkedinIcon className="w-3.5 h-3.5" />
              </a>
              <a
                href="#"
                className="w-7 h-7 rounded-full bg-white text-[#161616] flex items-center justify-center hover:bg-[#f25c05] hover:text-white transition-colors duration-200"
                aria-label="Facebook"
              >
                <FacebookIcon className="w-3.5 h-3.5" />
              </a>
              <a
                href="#"
                className="w-7 h-7 rounded-full bg-white text-[#161616] flex items-center justify-center hover:bg-[#f25c05] hover:text-white transition-colors duration-200"
                aria-label="Twitter"
              >
                <TwitterIcon className="w-3.5 h-3.5" />
              </a>
              <a
                href="#"
                className="w-7 h-7 rounded-full bg-white text-[#161616] flex items-center justify-center hover:bg-[#f25c05] hover:text-white transition-colors duration-200"
                aria-label="Instagram"
              >
                <InstagramIcon className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="lg:col-span-2">
            <h3 className="text-white font-semibold text-sm mb-3">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link, idx) => (
                <li key={idx}>
                  <Link to={link.href} className="hover:text-[#f25c05] transition-colors duration-150">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Services */}
          <div className="lg:col-span-2">
            <h3 className="text-white font-semibold text-sm mb-3">Services</h3>
            <ul className="space-y-2">
              {services.map((service, idx) => (
                <li key={idx}>
                  <Link to={service.href} className="hover:text-[#f25c05] transition-colors duration-150">
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Industries */}
          <div className="lg:col-span-2">
            <h3 className="text-white font-semibold text-sm mb-3">Industries</h3>
            <ul className="space-y-2">
              {industries.map((item, idx) => (
                <li key={idx}>
                  <Link to={item.href} className="hover:text-[#f25c05] transition-colors duration-150">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 5: Contact Us */}
          <div className="lg:col-span-2 space-y-3">
            <h3 className="text-white font-semibold text-sm mb-3">Contact Us</h3>
            
            <div className="flex items-start space-x-2.5">
              <MapPin className="w-4 h-4 text-[#f25c05] flex-shrink-0 mt-0.5" />
              <p className="leading-tight">
                No 33 Old Mahabaliburam Road,<br />
                Seevaram, Perungudi,<br />
                Chennai - 600 096, India
              </p>
            </div>

            <div className="flex items-center space-x-2.5">
              <Phone className="w-4 h-4 text-[#f25c05] flex-shrink-0" />
              <a href="tel:+919566663558" className="hover:text-[#f25c05] transition-colors">
                +91 95666 63558
              </a>
            </div>

            <div className="flex items-center space-x-2.5">
              <Mail className="w-4 h-4 text-[#f25c05] flex-shrink-0" />
              <a href="mailto:contactus@inim-ai.com" className="hover:text-[#f25c05] transition-colors">
                contactus@inim-ai.com
              </a>
            </div>

            <div className="flex items-center space-x-2.5">
              <Clock className="w-4 h-4 text-[#f25c05] flex-shrink-0" />
              <span>Mon - Sat: 9:00 AM - 6:00 PM</span>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800/80 py-4 text-[11px] text-gray-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left">
          <p>© 2026 Inimai Solutions. All Rights Reserved.</p>
          <div className="flex items-center space-x-4">
            <Link to="#" className="hover:text-gray-300 transition-colors">Privacy Policy</Link>
            <span>|</span>
            <Link to="#" className="hover:text-gray-300 transition-colors">Terms of Service</Link>
            <span>|</span>
            <Link to="#" className="hover:text-gray-300 transition-colors">Sitemap</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}